import { memo } from 'react';

import {
  EmployeeResponse,
  EmployeeTypeResponse,
  PlanningEntityType,
  TransportationMode
} from 'generated';
import { useTranslation } from 'react-i18next';
import { Box, styled } from '@mui/material';

import { formatSecondsToHoursMinutes } from 'utils/date';

import { BLUE_SECONDARY } from 'constants/colors';
import { StyledExtraSmallIcon } from 'components/shared/Icon/Icon.styles';

import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';

const Header = styled(Box)``;

const IconsSection = styled(Box)`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding: 0 5px;
`;

const IconBox = styled(Box)`
  display: flex;
  align-items: center;
  gap: 4px;
`;

interface Props {
  planningEntityType?: PlanningEntityType;
  productivity?: number;
  transportationMode?: TransportationMode;
  travelKilometers?: number;
  durationInSeconds?: number;
  hasDuty: boolean;
  employee?: EmployeeResponse;
  employeeType?: EmployeeTypeResponse;
  showEmployeeName?: boolean;
  showDuty?: boolean;
  showDurationDistanceTransport?: boolean;
  isOverrulingExternalDuty?: boolean;
}

const ActivitySummary = memo((props: Props) => {
  const {
    planningEntityType,
    productivity,
    transportationMode,
    travelKilometers,
    hasDuty,
    durationInSeconds,
    employee,
    showEmployeeName,
    showDuty,
    showDurationDistanceTransport,
    isOverrulingExternalDuty
  } = props;

  const { t } = useTranslation('common', {
    keyPrefix: 'activityPlanning'
  });

  const isRoute = planningEntityType === 'ROUTE';

  return (
    <>
      <Header
        display="flex"
        flexDirection="column"
        alignItems="space-between"
        sx={{
          position: 'relative',
          padding: '8px',
          paddingTop: 0,
          paddingBottom: 0,
          backgroundColor: BLUE_SECONDARY,
          // To hide the bottom corner radius of the sticky header
          ...(isRoute && {
            '&:before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '-5px',
              width: '100%',
              height: '5px',
              backgroundColor: BLUE_SECONDARY
            }
          })
        }}
      >
        {showDuty && (
          <Box p="0 5px">
            {(() => {
              let icon: IconType = 'triangle-exclamation';
              let text: string | undefined; // Ensure text is always a string
              let color: 'primary' | 'warning' = 'primary'; // Restrict color to expected values

              // Condition A: Determine the icon

              if (hasDuty) {
                if (employee) {
                  icon = 'user';
                  color = 'primary';
                  if (showEmployeeName) {
                    text = employee.nameInformal;
                  }
                } else {
                  icon = 'user-slash';
                  color = 'warning';
                  text = t('noEmployeeAssigned');
                }
              } else {
                icon = 'triangle-exclamation';
                text = t('noDutyAssigned');
                color = 'warning';
              }

              if (isOverrulingExternalDuty) {
                icon = 'rotate-exclamation';
                color = 'warning';
              }

              // Render only if an icon is determined
              return icon ? (
                <IconBox>
                  <StyledExtraSmallIcon type={icon} color={color} />
                  {text}
                </IconBox>
              ) : null;
            })()}
          </Box>
        )}
        {showDurationDistanceTransport && (
          <IconsSection mb={1}>
            {durationInSeconds !== undefined && (
              <IconBox>
                <StyledExtraSmallIcon type="clock" />
                {formatSecondsToHoursMinutes(durationInSeconds)}
              </IconBox>
            )}
            {travelKilometers !== undefined && (
              <IconBox>
                <StyledExtraSmallIcon
                  type={transportationMode == 'CAR' ? 'car-side' : 'bicycle'}
                />
                {travelKilometers}km
              </IconBox>
            )}
            {productivity !== undefined && (
              <IconBox>
                <StyledExtraSmallIcon type="chart-pie" />
                {(productivity * 100).toFixed()}%
              </IconBox>
            )}
          </IconsSection>
        )}
      </Header>
    </>
  );
});

export default ActivitySummary;
